import { useSyncExternalStore } from "react";
import { DocSync } from "@helm/core";
import {
  DEFAULT_ACCOUNT_RISK,
  DEFAULT_LIMITS,
  hasAnyLimit,
  sanitizeAccountRisk,
  sanitizeLimits,
  type AccountRiskJson,
  type PersonalLimitsJson,
} from "@helm/ui";

/**
 * Personal risk settings **per account** (owner Q-U2; SPEC §10.2, §19 `risk_settings.account_id`,
 * SPEC-ADDENDA A3), one synced document per account: `risk_settings/{accountId}` with body
 * `{ personal: PersonalLimits, enforce_drawdown }` (`AccountRiskJson`, the `EngineConfig` field
 * names). Server-authoritative: a 409 keeps the server copy. Limits start empty (Q-R8).
 *
 * The user-level `risk_settings/default` document from before per-account settings is read once
 * as a **template**: the form offers it for an account that has no document yet; it is never
 * copied anywhere without the user saving.
 *
 * **Enforced** (ADR-0020): the gateway loads each account's document before its session is READY,
 * applies `personal` and `enforce_drawdown` on top of the firm profile, hot-applies changes on
 * `DocChanged`, and refuses new orders (`RISK_CONFIG_INVALID`) while a stored document is invalid.
 */

/** The pre-per-account user-level document id. */
export const LEGACY_RISK_DOC_ID = "default";

export type AccountRiskEntry = {
  risk: AccountRiskJson;
  /** 0 = no document yet (never saved). */
  version: number;
  loaded: boolean;
  status: "idle" | "loading" | "saving" | "error";
  message: string | null;
};

export type RiskSettingsState = {
  byAccount: ReadonlyMap<string, AccountRiskEntry>;
  /** Limits from the old user-level document, when it had any set. */
  legacyTemplate: PersonalLimitsJson | null;
  legacyLoaded: boolean;
};

const EMPTY_ENTRY: AccountRiskEntry = {
  risk: DEFAULT_ACCOUNT_RISK,
  version: 0,
  loaded: false,
  status: "idle",
  message: null,
};

let state: RiskSettingsState = { byAccount: new Map(), legacyTemplate: null, legacyLoaded: false };
const listeners = new Set<() => void>();
const docs = new DocSync();

function emit(next: RiskSettingsState): void {
  state = next;
  for (const l of listeners) l();
}

function patch(accountId: string, p: Partial<AccountRiskEntry>): void {
  const m = new Map(state.byAccount);
  m.set(accountId, { ...(m.get(accountId) ?? EMPTY_ENTRY), ...p });
  emit({ ...state, byAccount: m });
}

export function riskEntry(accountId: string): AccountRiskEntry {
  return state.byAccount.get(accountId) ?? EMPTY_ENTRY;
}

const inflight = new Map<string, Promise<void>>();

/** Load (or reload) one account's document. Concurrent calls share one request. */
export function loadAccountRisk(accountId: string): Promise<void> {
  const running = inflight.get(accountId);
  if (running) return running;
  const p = (async () => {
    patch(accountId, { status: "loading", message: null });
    try {
      const doc = await docs.load<Record<string, unknown>>("risk_settings", accountId);
      patch(accountId, {
        risk: doc ? sanitizeAccountRisk(doc.body) : DEFAULT_ACCOUNT_RISK,
        version: doc?.version ?? 0,
        loaded: true,
        status: "idle",
      });
    } catch (e) {
      patch(accountId, {
        status: "error",
        message: e instanceof Error ? e.message : "could not load risk settings",
      });
    } finally {
      inflight.delete(accountId);
    }
  })();
  inflight.set(accountId, p);
  return p;
}

/** Read the old user-level document once, to offer it as a template. */
export async function loadLegacyTemplate(): Promise<void> {
  if (state.legacyLoaded) return;
  try {
    const doc = await docs.load<Record<string, unknown>>("risk_settings", LEGACY_RISK_DOC_ID);
    const limits = doc ? sanitizeLimits(doc.body) : null;
    emit({ ...state, legacyTemplate: limits && hasAnyLimit(limits) ? limits : null, legacyLoaded: true });
  } catch {
    emit({ ...state, legacyLoaded: true });
  }
}

export type SaveOutcome = { ok: boolean; message: string };

/** Save one account's settings (already validated). */
export async function saveAccountRisk(accountId: string, risk: AccountRiskJson): Promise<SaveOutcome> {
  if (!riskEntry(accountId).loaded) await loadAccountRisk(accountId);
  const cur = riskEntry(accountId);
  if (!cur.loaded) return { ok: false, message: `Not saved: ${cur.message ?? "could not load"}` };
  patch(accountId, { status: "saving", message: null });
  try {
    const r = await docs.save<AccountRiskJson>("risk_settings", accountId, cur.risk, cur.version, risk);
    if (r.kind === "saved") {
      patch(accountId, {
        risk: sanitizeAccountRisk(r.doc.body),
        version: r.doc.version,
        loaded: true,
        status: "idle",
      });
      return { ok: true, message: `Saved · v${String(r.doc.version)}` };
    }
    // Server-authoritative: another device saved first; show theirs.
    patch(accountId, { risk: sanitizeAccountRisk(r.server.body), version: r.server.version, status: "idle" });
    return {
      ok: false,
      message: "Changed on another device — their version is shown; review and save again.",
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : "save failed";
    patch(accountId, { status: "error", message });
    return { ok: false, message: `Not saved: ${message}` };
  }
}

/**
 * "Copy to all accounts": save `risk` to every account in `accountIds`, one document each, in
 * order. Returns a one-line summary naming any account that did not save.
 */
export async function copyRiskToAccounts(
  accountIds: readonly string[],
  risk: AccountRiskJson,
): Promise<SaveOutcome> {
  const failed: string[] = [];
  for (const id of accountIds) {
    const r = await saveAccountRisk(id, risk);
    if (!r.ok) failed.push(id);
  }
  const n = accountIds.length;
  return failed.length === 0
    ? { ok: true, message: `Copied to ${String(n)} account${n === 1 ? "" : "s"}` }
    : { ok: false, message: `Not saved for ${failed.join(", ")} — review and save again.` };
}

/** Dev preview only: show settings without the API (`version` 0 = "no document yet"). */
export function setAccountRiskLocal(accountId: string, risk: AccountRiskJson, version = 1): void {
  patch(accountId, { risk, loaded: true, version, status: "idle" });
}

/** Dev preview only: pretend the legacy template was loaded. */
export function setLegacyTemplateLocal(limits: PersonalLimitsJson | null): void {
  emit({ ...state, legacyTemplate: limits, legacyLoaded: true });
}

/** Sign-out: forget every account's settings. */
export function resetRiskSettings(): void {
  emit({ byAccount: new Map(), legacyTemplate: null, legacyLoaded: false });
}

/** Limits that apply to `accountId` right now (empty until loaded). */
export function limitsFor(s: RiskSettingsState, accountId: string | null): PersonalLimitsJson {
  if (accountId === null) return DEFAULT_LIMITS;
  return s.byAccount.get(accountId)?.risk.personal ?? DEFAULT_LIMITS;
}

const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = (): RiskSettingsState => state;

export function useRiskSettings(): RiskSettingsState {
  return useSyncExternalStore(subscribe, snapshot);
}
